/**
 * Timesheet Deadline Settings Component
 * Allows configuration of organization-wide submission deadlines
 */

import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from 'lucide-react';
import type { TimesheetDeadlineSettings as DeadlineSettings } from '@/types/timesheetSettings';

interface TimesheetDeadlineSettingsProps {
  settings: DeadlineSettings;
  onChange?: (settings: DeadlineSettings, isValid: boolean) => void;
  onSave?: (settings: DeadlineSettings) => void;
  onNavigateToNotifications?: () => void;
}

export const TimesheetDeadlineSettings: React.FC<TimesheetDeadlineSettingsProps> = ({
  settings,
  onChange,
  onSave,
  onNavigateToNotifications
}) => {
  const [localSettings, setLocalSettings] = useState<DeadlineSettings>(settings);

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      // Validate settings
      const isValid = 
        localSettings.deadlineTime !== '' &&
        localSettings.gracePeriodDays >= 0 &&
        localSettings.gracePeriodDays <= 7 &&
        (!localSettings.sendReminders || localSettings.reminderDaysBefore >= 1);
      
      onChange(localSettings, isValid);
    }
  }, [localSettings, onChange]);

  // Update local state when prop changes (for reset functionality)
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleChange = (field: keyof DeadlineSettings, value: any) => {
    setLocalSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-start gap-3 pb-4 border-b">
        <div className="rounded-lg bg-primary/10 p-2.5">
          <Calendar className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold">Submission Deadlines</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure when timesheets are due and grace period rules
          </p>
        </div>
      </div>

      {/* Deadline Configuration - Three Column Layout */}
      <div className="p-4 bg-muted/20 rounded-lg border space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Deadline Day */}
          <div className="space-y-2">
            <Label htmlFor="deadline-day" className="text-sm font-medium">
              Deadline Day
            </Label>
            <Select
              value={localSettings.deadlineDay.toString()}
              onValueChange={(value) => handleChange('deadlineDay', parseInt(value) as 0 | 1 | 2 | 3 | 4 | 5 | 6)}
            >
              <SelectTrigger id="deadline-day" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Sunday</SelectItem>
                <SelectItem value="1">Monday</SelectItem>
                <SelectItem value="2">Tuesday</SelectItem>
                <SelectItem value="3">Wednesday</SelectItem>
                <SelectItem value="4">Thursday</SelectItem>
                <SelectItem value="5">Friday</SelectItem>
                <SelectItem value="6">Saturday</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Which day of the week must timesheets be submitted by
            </p>
          </div>

          {/* Deadline Time */}
          <div className="space-y-2">
            <Label htmlFor="deadline-time" className="text-sm font-medium">
              Deadline Time
            </Label>
            <Input
              id="deadline-time"
              type="time"
              value={localSettings.deadlineTime}
              onChange={(e) => handleChange('deadlineTime', e.target.value)}
              className="h-10"
            />
            <p className="text-xs text-muted-foreground">
              Time on the deadline day when timesheets are due
            </p>
          </div>

          {/* Grace Period */}
          <div className="space-y-2">
            <Label htmlFor="grace-period" className="text-sm font-medium">
              Grace Period
            </Label>
            <div className="flex gap-2">
              <Input
                id="grace-period"
                type="number"
                min="0"
                max="7"
                value={localSettings.gracePeriodDays}
                onChange={(e) => handleChange('gracePeriodDays', parseInt(e.target.value) || 0)}
                className="h-10"
              />
              <div className="flex items-center px-3 text-sm text-muted-foreground whitespace-nowrap">
                days
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Days after deadline before marking as "late"
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};
